'use client';

import Link from 'next/link';
import { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Send } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

/**
 * The guest intake form (tracker row 3.3).
 *
 * Submission-first by design: there is no chat widget and no "start a
 * conversation" button. The guest describes the problem once, gets a reference,
 * and the reply arrives by email or SMS from a person.
 *
 * The form asks for the least it can: a type, a sentence, and one contact route.
 * Order context is not typed by the guest — it is carried in the link from the
 * order status page (`orderId` + `token`) and verified server-side. If those are
 * absent the case is still filed, honestly without a trace.
 */

export interface GuestCaseType {
  type: string;
  label: string;
  responseWindow: string;
}

interface GuestIntakeFormProps {
  types: GuestCaseType[];
  initialType?: string;
  orderId?: string;
  orderToken?: string;
}

type Result = {
  ref: string;
  state: string;
  responseWindow: string;
  channel: 'email' | 'sms' | 'in_app' | 'push' | 'none';
};

type Errors = Partial<Record<'type' | 'summary' | 'contact' | 'form', string>>;

const SUMMARY_LIMIT = 500;

export default function GuestIntakeForm({ types, initialType, orderId, orderToken }: GuestIntakeFormProps) {
  const defaultType = useMemo(
    () => types.find((entry) => entry.type === initialType)?.type ?? types[0]?.type ?? '',
    [types, initialType],
  );
  const [type, setType] = useState(defaultType);
  const [summary, setSummary] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  const selected = types.find((entry) => entry.type === type);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const next: Errors = {};
    if (!type) next.type = 'Choose what the request is about.';
    if (summary.trim().length < 3) next.summary = 'Describe what happened in a sentence.';
    if (summary.trim().length > SUMMARY_LIMIT) next.summary = `Keep it under ${SUMMARY_LIMIT} characters.`;
    if (!email.trim() && !phone.trim()) next.contact = 'Leave an email address or a phone number so we can reply.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setBusy(true);
    setErrors({});
    try {
      const response = await fetch('/api/public/support', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          type,
          summary: summary.trim(),
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
          ...(orderId && orderToken ? { orderId, orderToken } : {}),
          ...(website ? { website } : {}),
        }),
      });
      const payload = (await response.json().catch(() => null)) as
        | { ok?: boolean; error?: string; ref?: string; state?: string; responseWindow?: string; channel?: Result['channel'] }
        | null;
      if (!response.ok || !payload?.ok || !payload.ref) {
        setErrors({ form: payload?.error || 'We could not file that request. Try again in a moment.' });
        return;
      }
      setResult({
        ref: payload.ref,
        state: payload.state ?? 'new',
        responseWindow: payload.responseWindow ?? selected?.responseWindow ?? 'the window shown above',
        channel: payload.channel ?? 'email',
      });
    } catch {
      setErrors({ form: 'We could not reach the support desk. Check your connection and try again.' });
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <div className="space-y-5">
        <Alert>
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Your request is with us</AlertTitle>
          <AlertDescription>
            Reference <strong className="font-mono">{result.ref}</strong>. A person will come back to you within{' '}
            {result.responseWindow} by {result.channel === 'sms' ? 'text message' : 'email'}.
          </AlertDescription>
        </Alert>
        <p className="text-sm text-muted-foreground">
          Keep the reference. You can check progress with it and the {result.channel === 'sms' ? 'phone number' : 'email address'} you
          used.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button asChild>
            <Link href="/help/status">Check progress</Link>
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setResult(null);
              setSummary('');
              setErrors({});
            }}
          >
            File another request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="space-y-5" onSubmit={submit} noValidate>
      {errors.form ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>We could not file that request</AlertTitle>
          <AlertDescription>{errors.form}</AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="guest-type">What is this about?</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger id="guest-type">
            <SelectValue placeholder="Choose a topic" />
          </SelectTrigger>
          <SelectContent>
            {types.map((entry) => (
              <SelectItem key={entry.type} value={entry.type}>
                {entry.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selected ? (
          <p className="text-xs text-muted-foreground">
            We aim to reply within {selected.responseWindow}. The window pauses while we wait on you.
          </p>
        ) : null}
        {errors.type ? <p className="text-xs text-destructive">{errors.type}</p> : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="guest-summary">What happened?</Label>
        <Textarea
          id="guest-summary"
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          rows={5}
          maxLength={SUMMARY_LIMIT}
          placeholder="The order number if you have it, what went wrong, and what you would like us to do."
        />
        <p className="text-right text-xs text-muted-foreground">
          {summary.length}/{SUMMARY_LIMIT}
        </p>
        {errors.summary ? <p className="text-xs text-destructive">{errors.summary}</p> : null}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="guest-email">Email</Label>
          <Input
            id="guest-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="guest-phone">Phone</Label>
          <Input
            id="guest-phone"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+233 55 123 4567"
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Give at least one. We reply by email if you leave one, otherwise by text.
      </p>
      {errors.contact ? <p className="text-xs text-destructive">{errors.contact}</p> : null}

      <div className="space-y-2">
        <Label htmlFor="guest-name">First name (optional)</Label>
        <Input
          id="guest-name"
          autoComplete="given-name"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          maxLength={80}
          placeholder="So our reply does not start with &ldquo;Hi there&rdquo;"
        />
      </div>

      {/* Honeypot: invisible to people, irresistible to scripts. */}
      <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="guest-website">Website</label>
        <input
          id="guest-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
        />
      </div>

      {orderId && orderToken ? (
        <p className="rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Your order is attached to this request. We will see the same order you are looking at.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Replying about an order?{' '}
          <Link className="underline underline-offset-2" href="/help/status">
            Check the order status page
          </Link>{' '}
          first — the link there attaches the order automatically.
        </p>
      )}

      <Button type="submit" disabled={busy} className="w-full sm:w-auto">
        <Send className="mr-2 h-4 w-4" aria-hidden="true" />
        {busy ? 'Sending…' : 'Send request'}
      </Button>
    </form>
  );
}
